import { cn } from "../../../lib/utils";

/**
 * THE FACILITATOR OS CARD. Three tiers, one dimensional recipe.
 *
 * ===================== FIVE TRANSCRIPTIONS OF ONE TREATMENT ================
 * The dimensional lifted card (gradient surface, composite shadow with an inset
 * edge light, tinted border, blurred radial ambient glow) exists in exactly five
 * places and is hand-transcribed in every one: the command band, Coach's room
 * reading, Manage's section, and twice on Profile. Three radii (`rounded-2xl`,
 * `rounded-lg`, none) and two glow anchors (`-left-16 -top-16`, `-right-20
 * -top-20`) for one treatment, because each copy drifted from the last. The
 * gradient and the shadow are byte-identical in all five, which is what tells
 * you they were copied rather than designed apart.
 *
 * WHAT CONVERGES HERE. `rounded-lg` is the radius (the FOS's dominant value,
 * 125 usages against 96 `xl` and 53 `2xl`), `border-charlotte/[0.10]` is the
 * border, and the glow anchors top-left. Coach's room reading loses a radius
 * step and Manage's section loses a glow corner. Neither carries meaning; they
 * carry history.
 *
 * ===================== `bleed` IS ONE EXCEPTION, NOT A KNOB ================
 * The command band is a full-bleed section: it spans the shell, so a radius and
 * a box border would draw a card around the whole top of the page. It keeps the
 * gradient, the shadow and the glow, and trades the box border for the bottom
 * rule it ships today. That is the only shape variation this primitive offers.
 * Anything else would be the twenty-five background/alpha combinations coming
 * back with a prop name on them.
 *
 * ===================== THE LIFT BUDGET IS NOT THIS FILE'S TO SPEND =========
 * Coach allows exactly two lifted elements ("with the room reading that is two
 * lifted elements in the whole panel; a third would spend the signal") and
 * Manage allows one. Those budgets are design canon and they differ because the
 * pages' jobs differ. This primitive makes a lift cheap to WRITE, which is
 * precisely the risk: cheap lifts are how a page ends up with five. No consumer
 * gains a lift in this chunk. `SurfaceCard.test.jsx` counts the lifted mounts on
 * Coach and reddens at three.
 *
 * ===================== SECONDARY AND NESTED ARE THE OTHER TWO TIERS ========
 * Twenty-five distinct background-plus-alpha combinations are in use across four
 * surfaces; `bg-background-cloudburst` alone appears solid and at seven alphas.
 * There is no vocabulary, only per-call-site choices. Two survive: `secondary`
 * for a supporting panel on a page background, `nested` for a panel genuinely
 * inside another panel, one step darker so the nesting reads as depth rather
 * than as a second sibling.
 */

/**
 * EXPORTED BECAUSE jsdom CANNOT SEE IT. cssstyle silently DROPS a `background`
 * shorthand carrying a `linear-gradient`, so `node.style.background` is the empty
 * string in every test in this repo and the serialized `style` attribute does not
 * contain the gradient either. `boxShadow` and `filter` survive and are asserted
 * on the rendered node; the gradient can only be pinned as a value, so it is one.
 */
export const LIFTED_SURFACE = {
  background:
    "linear-gradient(135deg, rgba(19, 32, 57, 0.95) 0%, rgba(13, 21, 38, 0.98) 100%)",
  boxShadow:
    "0 4px 40px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.03)",
};

export const AMBIENT_GLOW = {
  background:
    "radial-gradient(circle, rgba(171, 235, 247, 0.05) 0%, transparent 70%)",
  filter: "blur(30px)",
};

/**
 * ADDITIVE DEPTH (design note P30, FE-9.8e). OPT-IN, AND THAT IS THE WHOLE
 * POINT OF THE SHAPE.
 *
 * Read from the student platform as it actually ships (`Dashboard/QuestCard.jsx`
 * and `Journey/Level.jsx`): the quality that reads as "additive space" is not one
 * gradient. It is TWO light sources, from TWO directions, added over a gradient
 * surface, with the content floating above them on its own layer. The glows ADD
 * light rather than darken, which is exactly what a flat panel cannot do.
 *
 * WHY A VARIANT AND NOT A BLANKER CHANGE TO `TIER.lifted` (codex F2). Every
 * lifted mount in the tree does not silently become richer. A consumer asks. That
 * keeps the blast radius equal to the list of mounts the application specs name,
 * and it means a regression traces to one call site rather than to a primitive.
 * `TeachingJourney`'s first-use mount is the concrete reason: it is the least
 * finished screen in the OS, it is FE-9.7's territory, and a blanket change would
 * have put new depth on a surface nobody has designed yet. It stays on
 * `LIFTED_SURFACE`, byte for byte, and `SurfaceCard.test.jsx` asserts that.
 *
 * PAINTED WITH GRADIENTS, NOT FILTERS. A multi-stop radial already has a soft
 * falloff and needs no `filter: blur()`, which would force its own compositing
 * layer. So the richer treatment is also the cheaper one: the current recipe
 * paints one glow carrying a 30px blur, this one paints two carrying none.
 *
 * DEPTH IS NOT ANIMATION. The student side animates one glow
 * (`CompletionCard.jsx`, `glowPulse`) and that stays on the student side: the
 * facilitator motion grammar (P21 to P25) reserves standing motion for work
 * waiting on the teacher. Every glow here is static, and a glow that animates is
 * a defect rather than a flourish.
 *
 * THE GEOMETRY IS INLINE RATHER THAN CLASSED, deliberately. jsdom resolves no
 * Tailwind, so an arbitrary-value class would be unassertable and would carry a
 * compile risk nothing in the suite could see. Inline `left`/`top`/`width` survive
 * `getComputedStyle` in jsdom, so the two corners and the absence of a filter are
 * pinned on the rendered node rather than argued.
 */
export const DEPTH_SURFACE = {
  background: LIFTED_SURFACE.background,
  boxShadow:
    "0 4px 40px rgba(0, 0, 0, 0.34), inset 0 1px 0 rgba(255, 255, 255, 0.055), inset 0 -1px 0 rgba(0, 0, 0, 0.25)",
};

/**
 * THE TWO LIGHTS, AND COACH IS THE NAMED EXCEPTION TO THE SECOND ONE.
 *
 * `additive` is the two-hue recipe: charlotte from the top left, persimmon from
 * the bottom right. `charlotte` is Coach's, and it is a design ruling rather than
 * a taste: the locked v2.2 contract gives persimmon exactly one job on that
 * surface, "presence may matter", so a decorative persimmon glow there would
 * spend an attention colour on ambience. Coach still gets two sources from two
 * directions; both are charlotte, the second at a lower alpha. Where a hue
 * carries a locked meaning, depth yields to the meaning.
 */
export const DEPTH_LIGHTS = {
  additive: {
    from: "radial-gradient(circle, rgba(171, 235, 247, 0.075) 0%, rgba(171, 235, 247, 0.03) 38%, transparent 72%)",
    to: "radial-gradient(circle, rgba(255, 90, 95, 0.05) 0%, rgba(255, 90, 95, 0.018) 38%, transparent 72%)",
  },
  charlotte: {
    from: "radial-gradient(circle, rgba(171, 235, 247, 0.075) 0%, rgba(171, 235, 247, 0.03) 38%, transparent 72%)",
    to: "radial-gradient(circle, rgba(171, 235, 247, 0.032) 0%, rgba(171, 235, 247, 0.012) 40%, transparent 72%)",
  },
};

/* THE CORNERS, AND THE CLIP IS INTENDED. `TIER.lifted` carries `overflow-hidden`,
   so each radial is cut at the card's own rounded border. That is the boundary
   the card already has rather than a hard edge introduced inside it. The
   geometry is the approved mock's, byte for byte (`fe9-8e-depth.html`,
   `.lift.dim::before/::after`: -70px offset, 230px box), and the arithmetic of
   it is worth stating precisely because a first draft of this note got it
   wrong: the circle's CENTRE lands 45px inside the card (-70 + 115). What
   keeps the corner soft is not the centre being outside; it is that the
   brightest stop is 0.075 alpha and the multi-stop falloff reaches transparent
   by 72%, so the roughly quarter-disc the clip removes takes the OUTER falloff
   with it and the corner reads as lit from beyond the card. A single-stop
   radial at a higher alpha in this same position WOULD put a visible core
   against the clip, which is why the stops and the offsets travel together. */
const DEPTH_CORNER = {
  from: { left: "-70px", top: "-70px", width: "230px", height: "230px" },
  to: { right: "-70px", bottom: "-70px", width: "230px", height: "230px" },
};

const TIER = {
  lifted: "relative overflow-hidden border border-charlotte/[0.10] rounded-lg",
  secondary:
    "rounded-lg border border-white/[0.06] bg-background-cloudburst/40",
  nested: "rounded-lg border border-white/[0.06] bg-background-midnight/40",
};

/* ONE HAIRLINE VALUE FOR THE SHELL'S SEAMS (FE-9.8b, S-1 and P3). The context
   strip closes with `border-white/5` and this band closed with
   `border-charlotte/[0.08]` about 60px below it, so the top of every OS surface
   read as two rules of two different weights rather than as one chrome object.
   The band is the only `bleed` consumer in the tree, so converging the value
   here reaches exactly the seam that was wrong. */
const BLEED = "relative overflow-hidden border-b border-white/5";

/** The blurred radial that makes a lifted card read as lit from above-left. */
const AmbientGlow = () => (
  <div
    aria-hidden="true"
    className="pointer-events-none absolute -left-16 -top-16 h-48 w-48"
    style={AMBIENT_GLOW}
  />
);

/** One static light source. No filter, by design: see `DEPTH_SURFACE` above. */
const DepthLight = ({ corner, background }) => (
  <div
    aria-hidden="true"
    data-depth-light={corner}
    className="pointer-events-none absolute"
    style={{ ...DEPTH_CORNER[corner], background }}
  />
);

export const SurfaceCard = ({
  tier = "secondary",
  as: Tag = "div",
  bleed = false,
  depth = null,
  className = "",
  contentClassName = "",
  children,
  ...rest
}) => {
  const lifted = tier === "lifted";

  if (!lifted) {
    return (
      <Tag className={cn(TIER[tier] ?? TIER.secondary, className)} {...rest}>
        {children}
      </Tag>
    );
  }

  /* AN UNKNOWN NAME IS NOT A SILENT DOWNGRADE TO ONE GLOW. `depth="charlote"`
     would otherwise render the shipped single-glow card and look merely
     unchanged, which is the failure mode that takes a week to notice. */
  const lights = depth ? DEPTH_LIGHTS[depth] : null;
  if (depth && !lights) {
    throw new Error(`SurfaceCard: unknown depth variant "${depth}"`);
  }

  return (
    <Tag
      className={cn(bleed ? BLEED : TIER.lifted, className)}
      style={lights ? DEPTH_SURFACE : LIFTED_SURFACE}
      /* THE VARIANT IS READABLE OFF THE RENDERED NODE, and it has to be. Both
         lights are gradients, and cssstyle DROPS a `background` shorthand
         carrying one, so `light.style.background` is the empty string in jsdom:
         a test asking "is this card's second light charlotte or persimmon" can
         only reach the exported constant, which is true regardless of what any
         mount chose. Found by a RED probe that switched Coach to the two-hue
         recipe and stayed green. This attribute is what makes the mount's own
         choice assertable. */
      data-depth={depth ?? undefined}
      {...rest}
    >
      {lights ? (
        <>
          <DepthLight corner="from" background={lights.from} />
          <DepthLight corner="to" background={lights.to} />
        </>
      ) : (
        <AmbientGlow />
      )}
      {/* The glow is absolutely positioned behind, so the content needs a
          stacking context of its own or the blur washes over the text. */}
      <div className={cn("relative z-10", contentClassName)}>{children}</div>
    </Tag>
  );
};

export default SurfaceCard;
